import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { useParams } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { simulationService } from '../../services/simulation.service';

const StoreTimeline = () => {
  const { runId, storeId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timelineData, setTimelineData] = useState(null);
  const [storeInfo, setStoreInfo] = useState(null);

  useEffect(() => {
    fetchData();
  }, [runId, storeId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch store timeline data
      const timelineResponse = await simulationService.getStoreTimeline(runId, storeId);
      setTimelineData(timelineResponse);

      // Fetch store details
      const storesData = await simulationService.getSimulationStores(runId);
      const store = storesData.find(s => s.store_id === storeId);
      setStoreInfo(store);

    } catch (err) {
      console.error('Error fetching store timeline data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Store Timeline - {storeInfo?.store_name || storeId}
      </Typography>

      {/* Store Information Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Store Details
              </Typography>
              <Typography variant="body2">
                <strong>Store ID:</strong> {storeInfo?.store_id}
              </Typography>
              <Typography variant="body2">
                <strong>Location:</strong> {storeInfo?.store_city}, {storeInfo?.store_state}
              </Typography>
              <Typography variant="body2">
                <strong>Address:</strong> {storeInfo?.store_location}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Current Status
              </Typography>
              <Typography variant="body2">
                <strong>Total On Hand:</strong> {formatNumber(timelineData?.current?.total_on_hand || 0)}
              </Typography>
              <Typography variant="body2">
                <strong>Total Pipeline:</strong> {formatNumber(timelineData?.current?.total_pipeline || 0)}
              </Typography>
              <Typography variant="body2">
                <strong>Total Target:</strong> {formatNumber(timelineData?.current?.total_target || 0)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Inventory Timeline Chart */}
      <Paper elevation={3} sx={{ p: 2, height: '500px' }}>
        <Typography variant="h6" gutterBottom>
          Inventory Timeline
        </Typography>
        <ResponsiveContainer width="100%" height="90%">
          <LineChart
            data={timelineData?.timeline || []}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis />
            <Tooltip
              formatter={(value) => formatNumber(value)}
              labelFormatter={formatDate}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="on_hand"
              name="On Hand"
              stroke="#2196f3"
              activeDot={{ r: 8 }}
            />
            <Line
              type="monotone"
              dataKey="pipeline"
              name="Pipeline"
              stroke="#4caf50"
            />
            <Line
              type="monotone"
              dataKey="target"
              name="Target"
              stroke="#ff9800"
              strokeDasharray="5 5"
            />
          </LineChart>
        </ResponsiveContainer>
      </Paper>

      {/* Additional Metrics */}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Order Metrics
              </Typography>
              <Typography variant="body2">
                <strong>Total Orders:</strong> {formatNumber(timelineData?.metrics?.total_orders || 0)}
              </Typography>
              <Typography variant="body2">
                <strong>Fill Rate:</strong> {((timelineData?.metrics?.fill_rate || 0) * 100).toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Shipment Metrics
              </Typography>
              <Typography variant="body2">
                <strong>Total Shipments:</strong> {formatNumber(timelineData?.metrics?.total_shipments || 0)}
              </Typography>
              <Typography variant="body2">
                <strong>Average Lead Time:</strong> {timelineData?.metrics?.average_lead_time?.toFixed(1) || 0} days
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Inventory Metrics
              </Typography>
              <Typography variant="body2">
                <strong>SKU Count:</strong> {formatNumber(timelineData?.metrics?.sku_count || 0)}
              </Typography>
              <Typography variant="body2">
                <strong>Stock Status:</strong> {timelineData?.metrics?.stock_status || 'Unknown'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StoreTimeline; 